'use client';

import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { cn } from '../../lib/cn';
import { resolveColor, type SColor } from '../../lib/color';
import { SDivider } from '../SDivider';
import { SGhostButton } from '../SGhostButton';
import { todayStr, monthCells, calcYearMonth, WEEK_LABELS } from '../../lib/date';

export interface SCalendarEventGroup {
  /** 도트 색상. 팔레트 키(`grey_65`, `red_95` …) 또는 임의 CSS 색상 */
  color: SColor;
  label: string;
  dates: string[];
}

export interface SCalendarProps {
  /** 선택 날짜 (YYYY-MM-DD) */
  value?: string | null;
  /** 선택 변경 (sdUpdate) */
  onValueChange?: (date: string) => void;
  /** 선택 가능 범위 [시작, 종료] (빈 문자열이면 제한 없음) */
  selectable?: [string, string];
  /** 이벤트 그룹 (날짜별 점) */
  events?: SCalendarEventGroup[];
  /** 보이는 연·월 변경 (sdViewChange) */
  onViewChange?: (v: { year: number; month: number }) => void;
  /** 카드 그림자 */
  elevated?: boolean;
  className?: string;
  style?: CSSProperties;
}

/** SCalendar — sd-calendar 포팅. 월 그리드 + 이벤트 점 + 범례. */
export function SCalendar({
  value,
  onValueChange,
  selectable,
  events,
  onViewChange,
  elevated = false,
  className,
  style,
}: SCalendarProps) {
  const today = todayStr();
  const base = value || today;
  const [y, setY] = useState(() => Number(base.split('-')[0]));
  const [m, setM] = useState(() => Number(base.split('-')[1]));

  useEffect(() => {
    const b = value || today;
    setY(Number(b.split('-')[0]));
    setM(Number(b.split('-')[1]));
  }, [value]);

  const cells = useMemo(() => monthCells(y, m), [y, m]);
  const eventMap = useMemo(() => {
    const map = new Map<string, { color: string; label: string }[]>();
    (events ?? []).forEach(g =>
      g.dates.forEach(d => {
        const arr = map.get(d) ?? [];
        arr.push({ color: g.color, label: g.label });
        map.set(d, arr);
      }),
    );
    return map;
  }, [events]);
  const legend = useMemo(() => {
    const seen = new Set<string>();
    const out: { color: string; label: string }[] = [];
    (events ?? []).forEach(g => {
      const k = `${g.color}|${g.label}`;
      if (!seen.has(k)) {
        seen.add(k);
        out.push({ color: g.color, label: g.label });
      }
    });
    return out;
  }, [events]);
  const hasEvents = (events?.length ?? 0) > 0;

  const view = (ny: number, nm: number) => {
    setY(ny);
    setM(nm);
    onViewChange?.({ year: ny, month: nm });
  };
  const isDisabled = (date: string) => {
    if (!selectable) return false;
    const [s, e] = selectable;
    if (s !== '' && date < s) return true;
    if (e !== '' && date > e) return true;
    return false;
  };
  const pick = (date: string) => {
    if (!isDisabled(date)) onValueChange?.(date);
  };

  return (
    <div
      className={cn(
        'inline-flex w-[328px] min-h-[328px] flex-col gap-[var(--cmp-datepicker-calendar-gap)] rounded-[var(--cmp-datepicker-calendar-radius)] bg-[var(--cmp-datepicker-calendar-bg)] p-[var(--cmp-datepicker-calendar-paddingXY)] select-none text-[color:var(--cmp-datepicker-calendar-day-default-text)]',
        elevated && 'shadow-[2px_2px_12px_2px_rgba(0,0,0,0.2)]',
        className,
      )}
      style={style}
    >
      {/* 헤더: 연도 / 월 네비 */}
      <div className="flex items-center justify-center gap-[var(--cmp-datepicker-calendar-header-gap)]">
        <div className="flex items-center gap-[var(--cmp-datepicker-calendar-gap)]">
          <SGhostButton
            icon="chevronLeft"
            size="xxs"
            ariaLabel="prevYear"
            onClick={() => view(y - 1, m)}
          />
          <span className="inline-flex min-w-[40px] items-center justify-center text-center text-[14px] font-medium leading-[24px]">
            {y}
          </span>
          <SGhostButton
            icon="chevronRight"
            size="xxs"
            ariaLabel="nextYear"
            onClick={() => view(y + 1, m)}
          />
        </div>
        <SDivider
          vertical
          className="h-[var(--cmp-datepicker-calendar-header-divider-height)] self-center"
        />
        <div className="flex w-full items-center gap-[var(--cmp-datepicker-calendar-gap)]">
          <SGhostButton
            icon="chevronLeft"
            size="xxs"
            ariaLabel="prevMonth"
            onClick={() => {
              const n = calcYearMonth(y, m, 'prev');
              view(n.year, n.month);
            }}
          />
          <span className="inline-flex w-full min-w-[40px] items-center justify-center text-center text-[14px] font-medium leading-[24px]">
            {m}월
          </span>
          <SGhostButton
            icon="chevronRight"
            size="xxs"
            ariaLabel="nextMonth"
            onClick={() => {
              const n = calcYearMonth(y, m, 'next');
              view(n.year, n.month);
            }}
          />
        </div>
      </div>

      {/* 요일 */}
      <div className="grid grid-cols-7">
        {WEEK_LABELS.map(label => (
          <span
            key={label}
            className="flex h-[20px] items-center justify-center text-[12px] font-normal text-[color:var(--cmp-datepicker-calendar-week-color)]"
          >
            {label}
          </span>
        ))}
      </div>

      {/* 날짜 그리드 */}
      <div className="grid grid-cols-7 gap-y-[var(--cmp-datepicker-calendar-grid-rowGap)]">
        {cells.map(cell => {
          const selected = cell.inCurrentMonth && value === cell.date;
          const isToday = cell.inCurrentMonth && today === cell.date;
          const disabled = cell.inCurrentMonth && isDisabled(cell.date);
          const evs = cell.inCurrentMonth ? eventMap.get(cell.date) : undefined;
          return (
            <button
              key={cell.date}
              type="button"
              disabled={!cell.inCurrentMonth || disabled}
              onClick={() => cell.inCurrentMonth && pick(cell.date)}
              className={cn(
                'flex min-h-[var(--cmp-datepicker-calendar-day-circle-size)] w-[var(--cmp-datepicker-calendar-day-width)] flex-col items-center justify-start border-none bg-transparent p-0',
                !cell.inCurrentMonth
                  ? 'invisible pointer-events-none'
                  : disabled
                    ? 'cursor-not-allowed'
                    : 'cursor-pointer',
              )}
              aria-hidden={!cell.inCurrentMonth ? 'true' : undefined}
            >
              <span
                className={cn(
                  'flex h-[var(--cmp-datepicker-calendar-day-circle-size)] w-[var(--cmp-datepicker-calendar-day-circle-size)] items-center justify-center rounded-[var(--cmp-datepicker-calendar-day-circle-radius)] border border-solid border-transparent text-[14px] leading-[var(--cmp-datepicker-calendar-day-circle-size)]',
                  selected
                    ? 'bg-[var(--cmp-datepicker-calendar-day-select-bg)] text-[color:var(--cmp-datepicker-calendar-day-select-text)]'
                    : disabled
                      ? 'text-[color:var(--cmp-datepicker-calendar-day-disabled-text)]'
                      : 'text-[color:var(--cmp-datepicker-calendar-day-default-text)] hover:border-[color:var(--cmp-datepicker-calendar-day-hover-border)] hover:text-[color:var(--cmp-datepicker-calendar-day-hover-text)]',
                  isToday && !disabled && 'font-bold',
                )}
              >
                {cell.inCurrentMonth ? cell.day : ''}
              </span>
              {hasEvents && (
                <span
                  className="flex min-h-[14px] w-full flex-wrap items-center justify-center gap-[var(--cmp-datepicker-calendar-day-dot-gap)] py-[var(--cmp-datepicker-calendar-day-dot-paddingY)]"
                  aria-hidden
                >
                  {evs?.map((ev, i) => (
                    <span
                      key={i}
                      className="h-[var(--cmp-datepicker-calendar-day-dot-size)] w-[var(--cmp-datepicker-calendar-day-dot-size)] rounded-full"
                      style={{ backgroundColor: resolveColor(ev.color) }}
                    />
                  ))}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* 범례 */}
      {legend.length > 0 && (
        <>
          <SDivider />
          <div className="flex flex-wrap items-center justify-center gap-x-[var(--cmp-datepicker-calendar-legend-group-gap)] gap-y-[4px] text-[11px] leading-[18px]">
            {legend.map(item => (
              <span
                key={`${item.color}|${item.label}`}
                className="inline-flex items-center gap-[var(--cmp-datepicker-calendar-legend-gap)]"
              >
                <span
                  className="h-[var(--cmp-datepicker-calendar-day-dot-size)] w-[var(--cmp-datepicker-calendar-day-dot-size)] rounded-full"
                  style={{ backgroundColor: resolveColor(item.color) }}
                  aria-hidden
                />
                <span>{item.label}</span>
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
